import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { useOutletContext } from 'react-router'
import { FaArrowLeft } from 'react-icons/fa'
import clsx from 'clsx'
import DOMPurify from 'dompurify'
import { FaUser, FaRegComment, FaRegHeart, FaHeart } from 'react-icons/fa'
import { LuDot } from 'react-icons/lu'

import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import {
  useGetPostByIdQuery,
  useUpdatePostLikesMutation,
  useDeletePostMutation,
} from '../../services/apiSlice'
import { CurrentUser } from '../../services/schema/User'
import Filler from '../../components/utils/Filler'
import CustomCreateReplyWrapper from '../../components/custom/wrapper/CustomCreateReplyWrapper'
import ReplyByPost from '../../components/reply/ReplyByPost'
import NotFound from '../../components/fallback/NotFound'
import { stringShortener } from '../../services/helpers'

const Post: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [updatePostLikes, { isError: isErrorLike }] =
    useUpdatePostLikesMutation()
  const [deletePost, { isSuccess: deleteSuccess }] = useDeletePostMutation()
  const [debouncer, setDebouncer] = useState<
    ReturnType<typeof setTimeout> | undefined
  >(undefined)
  const user = useOutletContext<CurrentUser>()
  const prevLocUrl = useAppSelector((state) => state.app.prevLocation.url)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const username = location.pathname.split('/')[1]
  const id = location.pathname.split('/')[2]

  const [isLiked, setIsLiked] = useState(
    user.likedPosts?.includes(id as string),
  )

  useEffect(() => {
    if (isErrorLike) {
      setIsLiked(!isLiked)
    }

    if (deleteSuccess) {
      navigate(`${prevLocUrl === null ? '/home' : prevLocUrl}`, {
        replace: true,
      })
    }
  }, [isErrorLike, deleteSuccess])

  const { data, isSuccess } = useGetPostByIdQuery(
    {
      postId: id,
      username: username,
    },
    { skip: !username || !id },
  )

  const post = isSuccess && data && data

  if (!isSuccess) {
    return (
      <>
        <NotFound />
      </>
    )
  } else if (isSuccess && post) {
    const profilePic =
      post.profilePicture !== null ? post.profilePicture : '/user_sample.jpg'
    const postReplyCount = post?.replyCount as number
    const postLikeCount = post?.likeCount as number

    const usernameShort = stringShortener(post.username, 15)
    const displayNameShort = stringShortener(post.displayName, 15)

    const toggleLike = () => {
      const newState = !isLiked
      setIsLiked(newState)

      if (debouncer) {
        clearTimeout(debouncer)
        setDebouncer(undefined)
        return
      }

      const timeout = setTimeout(() => {
        // Place API request here...
        updatePostLikes({
          postId: id,
          username: username,
          type: `${newState ? 'add' : 'remove'}`,
        })
        setDebouncer(undefined)
      }, 1000)

      setDebouncer(timeout)
    }

    const sanitized =
      post?.postText !== null
        ? DOMPurify.sanitize(post?.postText as string)
        : null

    const media = post?.postMedia ? post.postMedia.split(',') : null
    const mediaTypes = post?.mediaTypes ? post.mediaTypes.split(',') : null
    let mediaIsImage: boolean[] = []

    let mediaDisplay

    if (media && mediaTypes) {
      mediaTypes.forEach((m) => {
        if (m === 'image') {
          mediaIsImage.push(true)
        } else {
          mediaIsImage.push(false)
        }
      })
      mediaDisplay = media.map((m, idx) => {
        if (mediaTypes[idx] === 'video') {
          return (
            <div
              key={m}
              className="relative flex h-full w-full grow flex-col items-center justify-center rounded-[30px] object-contain pr-[5px] pb-[5px]"
            >
              <video className="h-full w-full rounded-[30px]" controls>
                <source src={m} />
              </video>
            </div>
          )
        }

        if (mediaTypes[idx] === 'image') {
          const slideCount: number =
            mediaIsImage[0] && mediaIsImage[1] ? idx + 1 : 1
          const inline = {
            backgroundImage: 'url(' + m + ')',
          }
          return (
            <div
              key={m}
              className="relative flex h-full w-full min-w-0 grow cursor-pointer flex-col items-center justify-center rounded-[30px] object-contain pr-[5px] pb-[5px]"
              onClick={() => {
                navigate(
                  `/media/post?user=${post.username}&id=${post.postId}&slide=${slideCount}`,
                )
              }}
            >
              <div
                style={inline}
                className="absolute inset-x-0 inset-y-0 h-full w-full min-w-0 rounded-[30px] bg-cover bg-center bg-no-repeat"
              />
              {/* I patterned this layout from twitter/x itself and noticed 2 separate elements displaying the src... why do we need a separate div though if img does the job? maybe for the alt? or for screen readers / aria? */}
              <img
                className="absolute inset-x-0 inset-y-0 h-full w-full min-w-0 rounded-[30px] opacity-0"
                src={m}
                alt={m}
              />
            </div>
          )
        }
      })
    }

    return (
      <>
        <div className="relative flex w-full grow">
          <div className="h-dvh" />
          <div className="border-theme bgd-theme relative flex h-full w-full max-w-[600px] min-w-0 grow flex-col items-stretch border-r border-b border-l md:mr-0">
            <div className="border-theme sticky top-0 z-[100] flex h-[60px] w-full items-center justify-between border-b bg-inherit pl-[10px] text-xl font-bold">
              <div className="flex items-center">
                <button
                  onClick={() => {
                    navigate(`${prevLocUrl === null ? '/home' : prevLocUrl}`, {
                      replace: true,
                    })
                  }}
                  className="hover-main flex h-[40px] w-[40px] items-center justify-center rounded-full text-sm"
                >
                  <FaArrowLeft />
                </button>
                <p className="pl-[20px] tracking-wide">Post</p>
              </div>

              {user?.username === username && (
                <div>
                  <button
                    type="button"
                    className="border-theme bgd-theme-wb text-main-invert mr-[15px] cursor-pointer rounded-full border px-[15px] py-[5px] text-sm disabled:cursor-not-allowed disabled:opacity-50"
                    // disabled={true}
                    onClick={() => {
                      if (
                        confirm('Are you sure you want to delete this post?')
                      ) {
                        deletePost({
                          postId: id,
                          username: username,
                        })
                      }
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
            <section className="h-auto w-full">
              <article className="h-auto w-full px-[15px]">
                <div className="border-theme flex h-auto border-b pt-[10px]">
                  <div className="mr-[8px] h-auto w-[50px] shrink-0">
                    <div
                      data-id="profile-picture"
                      onClick={() => {
                        navigate(`/${data.username}`, { replace: true })
                      }}
                      className="flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-full bg-cover bg-center"
                      style={{
                        backgroundImage: `url(${profilePic})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                    />
                  </div>
                  <div className="flex h-auto w-full flex-col">
                    <div className="flex w-full">
                      <button
                        data-typeid="post-profile-btn"
                        onClick={() => {
                          navigate(`/${data.username}`, {
                            replace: true,
                          })
                        }}
                        className="flex cursor-pointer items-center"
                      >
                        <div className="hover:underline hover:underline-offset-2">
                          <p>{displayNameShort}</p>
                        </div>
                        <span className="text-blue pl-[5px]">
                          <FaUser />
                        </span>
                      </button>
                      <button
                        data-typeid="post-profile-btn"
                        onClick={() => {
                          navigate(`/${data.username}`, {
                            replace: true,
                          })
                        }}
                        className="text-sub cursor-pointer pl-[5px]"
                      >
                        <div>
                          <p>@{usernameShort}</p>
                        </div>
                      </button>
                    </div>
                    <div
                      className="w-full pt-[5px] pb-[10px]"
                      dangerouslySetInnerHTML={{ __html: sanitized as string }}
                    />
                    <div
                      className={clsx(
                        'relative flex w-full',
                        media?.length === 1 && 'h-[450px] lg:h-[500px]',
                        media?.length === 2 && 'h-[300px]',
                      )}
                    >
                      {mediaDisplay}
                    </div>
                    <Filler />
                    <div className="text-sub flex h-auto w-full items-center py-[10px] text-sm">
                      <p>{post.timeline.time}</p>
                      <LuDot />
                      <p>{post.timeline.long}</p>
                    </div>
                    <div className="text-sub border-theme flex h-auto w-full justify-evenly border-t pt-[5px] pb-[8px]">
                      <div className="flex h-auto w-auto grow items-center">
                        <button
                          data-typeid="post-reply-btn"
                          type="button"
                          className="hover-comment relative flex h-[30px] w-auto cursor-pointer items-center"
                        >
                          <div className="flex h-[30px] w-[30px] items-center justify-center rounded-full">
                            <FaRegComment />
                          </div>
                          <p className="flex items-center">{postReplyCount}</p>
                        </button>
                      </div>
                      <div className="flex h-auto w-auto grow items-center">
                        <button
                          data-typeid="post-like-btn"
                          type="button"
                          className="hover-like items-center% relative flex h-[30px] w-auto cursor-pointer"
                          onClick={() => {
                            toggleLike()
                          }}
                        >
                          <div className="flex h-[30px] w-[30px] items-center justify-center rounded-full">
                            {isLiked ? (
                              <FaHeart className="text-red" />
                            ) : (
                              <FaRegHeart />
                            )}
                          </div>
                          <p className="flex items-center">{postLikeCount}</p>
                        </button>
                      </div>
                      <div className="relative flex h-[30px] w-auto grow items-center"></div>
                    </div>
                  </div>
                </div>
              </article>
              <CustomCreateReplyWrapper />
              <ReplyByPost postId={id} />
            </section>
          </div>
        </div>
      </>
    )
  }
}

export default Post
